import { useTranslations } from "use-intl";
import { z } from "zod";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import {
  IncidentClosedOutcome,
  IncidentStatus,
  UpdateIncidentInput,
} from "@/gql/graphql";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { IncidentRecord } from "@/app/Incidents/GraphQL/incidentRecordQuery";
import { updateIncidentMutation } from "@/app/Incidents/GraphQL/updateIncidentMutation";
import { DateTimeInput } from "@/lib/Components/Form/Inputs/DateTimeInput";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";

type ClaimFormValues = UpdateIncidentInput & {
  rejection_reason: string;
  claim_opened_at: string | null;
};

export function IncidentClaimCard({ incident }: { incident: IncidentRecord }) {
  const t = useTranslations("incident");
  const { isAdmin } = useTenant();
  const { mutateAsync } = useGqlMutation(updateIncidentMutation);

  if (!incident.claim_opened_at) {
    return (
      <RecordScreenCard title={t("details.claim")}>
        <p className="text-sm text-gray-500">
          {incident.status === IncidentStatus.Reported
            ? t("details.noClaimYet")
            : t("details.closedWithoutClaim")}
        </p>
      </RecordScreenCard>
    );
  }

  const isRejected = incident.closed_outcome === IncidentClosedOutcome.Rejected;
  const schema = z.object({
    excess_amount: z
      .number({ error: t("claim.excessRequired") })
      .int()
      .min(1, t("claim.excessRequired")),
  });

  return (
    <RecordScreenFormCard<ClaimFormValues>
      title={t("details.claim")}
      subtitle={t("details.claimSubtitle")}
      isEditable={isAdmin && incident.status === IncidentStatus.ClaimOpen}
      validationSchema={toFormikValidationSchema(schema)}
      initialValues={{
        excess_amount: incident.excess_amount,
        insurer: incident.insurer ?? "",
        claim_reference: incident.claim_reference ?? "",
        rejection_reason: incident.rejection_reason ?? "",
        claim_opened_at: incident.claim_opened_at,
      }}
      onSubmit={async (values) => {
        await mutateAsync({
          id: incident.id,
          input: {
            excess_amount: values.excess_amount,
            insurer: values.insurer || null,
            claim_reference: values.claim_reference || null,
          },
        });
      }}
    >
      <CurrencyInput
        name="excess_amount"
        label={t("claim.excessAmount")}
        className="col-span-4"
        currency={incident.currency}
      />
      <TextInput
        name="insurer"
        label={t("claim.insurer")}
        className="col-span-4"
      />
      <TextInput
        name="claim_reference"
        label={t("claim.claimReference")}
        className="col-span-4"
      />
      <DateTimeInput
        name="claim_opened_at"
        label={t("claim.openedAt")}
        className="col-span-4"
        isEditable={false}
        viewNode={formatDateTimeLocal(incident.claim_opened_at)}
      />
      <ClaimDocuments incident={incident} />
      {isRejected ? (
        <TextAreaInput
          name="rejection_reason"
          label={t("claim.rejectionReason")}
          className="col-span-full"
          isEditable={false}
        />
      ) : null}
    </RecordScreenFormCard>
  );
}

function ClaimDocuments({ incident }: { incident: IncidentRecord }) {
  const t = useTranslations("incident.claim");
  const documents = incident.claimDocuments ?? [];

  return (
    <div className="col-span-full">
      <p className="text-sm font-medium text-gray-900">{t("documents")}</p>
      {documents.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {documents.map((document) => (
            <li key={document.id}>
              <a
                href={document.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
              >
                <PaperClipIcon className="size-4" />
                {document.file_name}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-gray-500">{t("noDocuments")}</p>
      )}
    </div>
  );
}
